import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/site/Logo";
import { CONTACT, NAV_LINKS } from "@/lib/contact";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-navbar" className="fixed top-0 left-0 right-0 z-50">
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#2B2420] text-white text-[12px]">
        <div className="px-6 md:px-10 lg:px-16 h-10 flex items-center justify-between">
          <div className="flex items-center gap-6 text-white/85">
            <a
              href={`tel:${CONTACT.primary.tel}`}
              data-testid="topbar-phone-primary"
              className="flex items-center gap-2 hover:text-[#C9A24C] transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-[#C9A24C]" />
              <span className="text-white/60 uppercase tracking-[0.2em] text-[10px]">{CONTACT.primary.name}</span>
              <span className="font-semibold">{CONTACT.primary.display}</span>
            </a>
            <a
              href={`tel:${CONTACT.secondary.tel}`}
              data-testid="topbar-phone-secondary"
              className="hidden lg:flex items-center gap-2 hover:text-[#C9A24C] transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-[#C9A24C]" />
              <span className="text-white/60 uppercase tracking-[0.2em] text-[10px]">{CONTACT.secondary.name}</span>
              <span className="font-semibold">{CONTACT.secondary.display}</span>
            </a>
          </div>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="topbar-email"
            className="flex items-center gap-2 text-white/85 hover:text-[#C9A24C] transition-colors"
          >
            <Mail className="w-3.5 h-3.5 text-[#C9A24C]" />
            <span>{CONTACT.email}</span>
          </a>
        </div>
      </div>

      {/* Main bar */}
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl border-b border-[#E5DDD0] shadow-[0_2px_20px_-12px_rgba(15,23,42,0.18)]"
            : "bg-white border-b border-[#E5DDD0]"
        }`}
      >
        <div className="px-6 md:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo-link" aria-label="CONSTANTINOV LTD home">
            <Logo variant="dark" size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-sm font-semibold">
            {NAV_LINKS.map((l) => {
              const active = pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to));
              const isHash = l.to.includes("#");
              const classes = `transition-colors ${
                active && !isHash ? "text-[#B8732C]" : "text-[#2B2420] hover:text-[#B8732C]"
              }`;
              const testid = `nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`;
              return isHash ? (
                <a key={l.to} href={l.to} data-testid={testid} className={classes}>
                  {l.label}
                </a>
              ) : (
                <Link key={l.to} to={l.to} data-testid={testid} className={classes}>
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${CONTACT.primary.tel}`}
              data-testid="nav-call-btn"
              className="hidden xl:flex items-center gap-2 border border-[#2B2420] hover:bg-[#2B2420] hover:text-white text-[#2B2420] px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <Phone className="w-3.5 h-3.5" /> Call Ion
            </a>
            <a
              href="#quote"
              data-testid="nav-quote-btn"
              className="inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors"
            >
              Request a Free Quote <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <button
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 -mr-2 text-[#2B2420]"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden md:hidden bg-white border-b border-[#E5DDD0] px-6 py-5 space-y-2 shadow-lg"
        >
          {NAV_LINKS.map((l) => {
            const isHash = l.to.includes("#");
            return isHash ? (
              <a
                key={l.to}
                href={l.to}
                onClick={() => setOpen(false)}
                className="block py-2 text-base font-bold text-[#2B2420] hover:text-[#B8732C]"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block py-2 text-base font-bold text-[#2B2420] hover:text-[#B8732C]"
              >
                {l.label}
              </Link>
            );
          })}
          <div className="pt-3 border-t border-[#E5DDD0] space-y-2 text-sm">
            <a href={`tel:${CONTACT.primary.tel}`} className="flex items-center gap-2 text-[#2B2420] py-1">
              <Phone className="w-4 h-4 text-[#B8732C]" />
              <span className="text-[#8A7F74] uppercase tracking-[0.2em] text-[10px]">{CONTACT.primary.name}</span>
              <span className="font-bold">{CONTACT.primary.display}</span>
            </a>
            <a href={`tel:${CONTACT.secondary.tel}`} className="flex items-center gap-2 text-[#2B2420] py-1">
              <Phone className="w-4 h-4 text-[#B8732C]" />
              <span className="text-[#8A7F74] uppercase tracking-[0.2em] text-[10px]">{CONTACT.secondary.name}</span>
              <span className="font-bold">{CONTACT.secondary.display}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 text-[#2B2420] py-1 break-all">
              <Mail className="w-4 h-4 text-[#B8732C]" /> {CONTACT.email}
            </a>
          </div>
          <a
            href="#quote"
            onClick={() => setOpen(false)}
            className="block w-full text-center bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-5 py-4 text-sm font-bold uppercase tracking-wider mt-3"
          >
            Request a Free Quote
          </a>
        </div>
      )}
    </header>
  );
}
